import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "lucide-react";
import FooterNew from "../components/FooterNew";
import { coreTech, advancedCapabilities, processStepsData as processSteps, techPartners, industryApplications } from "../data/mockData";

const Services = () => {
  return (
    <main className="bg-primary">
      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-8 sm:pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full opacity-15 blur-3xl" style={{ background: "radial-gradient(circle, #7C4DFF, transparent)" }} />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full opacity-10 blur-3xl" style={{ background: "radial-gradient(circle, #A78BFA, transparent)" }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-[2.5rem] font-bold text-white mb-5 leading-tight">
              AI Services & <span className="gradient-text">Solutions</span>
            </h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
              Comprehensive AI Solutions for Enterprise Transformation
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Technologies */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Core AI <span className="gradient-text">Technologies</span>
            </h2>
          </motion.div>
          <div className="grid lg:grid-cols-3 gap-6">
            {coreTech.map((tech, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                whileHover={{ y: -6 }}
                className="rounded-2xl p-8 group"
                style={{ background: "rgba(10, 16, 41, 0.4)", backdropFilter: "blur(12px)", border: "1px solid rgba(124, 77, 255, 0.1)" }}
              >
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ background: `${tech.color}15`, border: `1px solid ${tech.color}30` }}>
                  <tech.icon className="w-7 h-7" style={{ color: tech.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-5 group-hover:text-ai-glow transition-colors">{tech.title}</h3>
                <ul className="space-y-3 list-none p-0 m-0">
                  {tech.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-400">
                      <ArrowRight className="w-3.5 h-3.5 mt-1 flex-shrink-0" style={{ color: tech.color }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advanced Capabilities */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Advanced AI <span className="gradient-text">Capabilities</span>
            </h2>
          </motion.div>
          <div className="grid lg:grid-cols-3 gap-6">
            {advancedCapabilities.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15, duration: 0.5 }}
                whileHover={{ y: -6 }}
                className="rounded-2xl p-8 group"
                style={{ background: "rgba(10, 16, 41, 0.4)", backdropFilter: "blur(12px)", border: "1px solid rgba(124, 77, 255, 0.1)" }}
              >
                <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full mb-4" style={{ background: `${card.color}15`, color: card.color, border: `1px solid ${card.color}30` }}>
                  {card.tag}
                </span>
                <h3 className="text-xl font-bold text-white mb-3">{card.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-4">{card.desc}</p>
                <ul className="space-y-2 list-none p-0 m-0">
                  {card.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-slate-400">
                      <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: card.color }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Implementation <span className="gradient-text">Process</span>
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6">
            {processSteps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="rounded-2xl p-6 group"
                style={{ background: "rgba(10, 16, 41, 0.4)", border: "1px solid rgba(124, 77, 255, 0.1)" }}
              >
                <div className="flex items-center gap-4 mb-3">
                  <span className="text-3xl font-black" style={{ color: step.color, opacity: 0.4 }}>{step.num}</span>
                  <h3 className="text-lg font-bold text-white">{step.title}</h3>
                </div>
                <div className="w-10 h-0.5 mb-3 rounded-full" style={{ background: step.color }} />
                <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Partners */}
      <section className="section-padding">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Technology <span className="gradient-text">Partnerships</span>
            </h2>
            <p className="text-sm sm:text-base text-slate-400 max-w-2xl mx-auto leading-relaxed">We work with leading technology platforms to deliver the best solutions</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {techPartners.map((tech, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                className="rounded-2xl p-6 text-center"
                style={{ background: "rgba(10, 16, 41, 0.4)", border: "1px solid rgba(124, 77, 255, 0.1)" }}
              >
                <tech.icon className="w-8 h-8 text-accent mx-auto mb-4" />
                <h3 className="text-lg font-bold text-white mb-2">{tech.title}</h3>
                <p className="text-sm text-slate-400">{tech.items}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              Industry <span className="gradient-text">Applications</span>
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {industryApplications.map((ind, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.4 }}
                whileHover={{ y: -4 }}
                className="rounded-2xl p-6 group"
                style={{ background: "rgba(10, 16, 41, 0.4)", border: "1px solid rgba(124, 77, 255, 0.1)" }}
              >
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-ai-glow transition-colors">{ind.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{ind.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <FooterNew />
    </main>
  );
};

export default Services;
